import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CalendarMonth, Check, Close, Person } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import AppNavigation from './AppNavigation';
import { scheduledDivesService } from '../services/scheduledDivesService';
import { authService } from '../services/authService';
import type { ScheduledDive } from '../types/ScheduledDive';

const parseDiveDate = (iso: string) => parseISO(iso.slice(0, 10));

const DiveCalendarJoin: React.FC = () => {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const currentUser = authService.getUser();

  const [dive, setDive] = useState<ScheduledDive | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState(
    currentUser ? `${currentUser.firstName ?? ''} ${currentUser.lastName ?? ''}`.trim() : ''
  );
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState<'accepted' | 'declined' | null>(null);
  const [responseError, setResponseError] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteCode) {
      setError('Invalid invite link.');
      setLoading(false);
      return;
    }
    scheduledDivesService.getByInviteCode(inviteCode)
      .then((d) => { setDive(d); setLoading(false); })
      .catch((err) => { setError(err?.message ?? 'Invite not found or expired.'); setLoading(false); });
  }, [inviteCode]);

  const respond = async (status: 'accepted' | 'declined') => {
    if (!inviteCode || !dive) return;
    if (!currentUser) {
      setResponseError('You need to be signed in to respond to an invite.');
      return;
    }
    if (!displayName.trim()) {
      setResponseError('Please enter your name so the organizer knows who you are.');
      return;
    }

    setResponding(true);
    setResponseError(null);
    try {
      const updated = await scheduledDivesService.respondToInvite(inviteCode, {
        displayName: displayName.trim(),
        status,
      });
      setDive(updated);
      setResponded(status);
    } catch (err: any) {
      setResponseError(err?.message ?? 'Failed to respond.');
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <AppNavigation />
        <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>
      </Box>
    );
  }

  if (error || !dive) {
    return (
      <Box>
        <AppNavigation />
        <Box maxWidth={480} mx="auto" mt={6}>
          <Alert severity="error">{error ?? 'Invite not found.'}</Alert>
          <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>Back to Calendar</Button>
        </Box>
      </Box>
    );
  }

  const isOwner = currentUser?.userId === dive.ownerUserId;
  const myInvite = dive.invitees.find((i) => i.userId === currentUser?.userId);
  const accepted = dive.invitees.filter((i) => i.status === 'accepted');
  const alreadyResponded = responded !== null || !!myInvite;

  return (
    <Box>
      <AppNavigation />
      <Box maxWidth={520} mx="auto" mt={4} px={2}>
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <CalendarMonth color="primary" />
              <Typography variant="h5" fontWeight={700}>Dive Invitation</Typography>
            </Stack>
            <Typography color="text.secondary">
              {dive.ownerDisplayName} has invited you to join a dive.
            </Typography>
          </Box>

          <Card variant="outlined">
            <CardContent>
              <Stack spacing={1.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>DATE</Typography>
                  <Typography fontWeight={600}>
                    {format(parseDiveDate(dive.scheduledDate), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                </Box>
                {dive.siteName && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>DIVE SITE</Typography>
                    <Typography>{dive.siteName}</Typography>
                  </Box>
                )}
                {dive.charterCompany && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>CHARTER</Typography>
                    <Typography>{dive.charterCompany}</Typography>
                  </Box>
                )}
                {dive.notes && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>NOTES</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{dive.notes}</Typography>
                  </Box>
                )}
                {accepted.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>GOING</Typography>
                    <Stack mt={0.5} spacing={0.25}>
                      {accepted.map((inv) => (
                        <Stack key={inv.userId} direction="row" alignItems="center" spacing={0.5}>
                          <Person fontSize="small" color="action" />
                          <Typography variant="body2">{inv.displayName}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          {isOwner && (
            <Alert severity="info">You are the organizer of this dive.</Alert>
          )}

          {!isOwner && !alreadyResponded && currentUser && (
            <Stack spacing={2}>
              <Divider />
              <Typography variant="subtitle1" fontWeight={600}>Will you join?</Typography>
              <TextField
                label="Your name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: 80 }}
              />
              {responseError && <Alert severity="error">{responseError}</Alert>}
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<Check />}
                  onClick={() => respond('accepted')}
                  disabled={responding}
                  fullWidth
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<Close />}
                  onClick={() => respond('declined')}
                  disabled={responding}
                  fullWidth
                >
                  Decline
                </Button>
              </Stack>
            </Stack>
          )}

          {!currentUser && (
            <Alert severity="warning">
              Sign in to accept or decline this invite, so your response appears on the organizer's calendar.
            </Alert>
          )}

          {alreadyResponded && !isOwner && (
            <Alert severity={responded === 'accepted' || myInvite?.status === 'accepted' ? 'success' : 'info'}>
              {responded === 'accepted' || myInvite?.status === 'accepted'
                ? "You're going! This dive has been added to your calendar."
                : "You declined this invite."}
            </Alert>
          )}

          <Button variant="outlined" onClick={() => navigate('/')}>View Calendar</Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default DiveCalendarJoin;
